import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, authFetch, useAuth } from "@/App";
import PageLayout, { PageHeader } from "@/components/PageLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Search, Star, MapPin, Phone, Mail, ChevronRight, 
  Dog, Heart, Home, Shield, Award, Calendar, Filter, X, ArrowLeft,
  CheckCircle, Clock, Users, Briefcase, Sparkles
} from "lucide-react";
import BookingDialog from "@/components/BookingDialog";

// Service categories for HOME SERVICES (not map-based)
const categories = [
  { id: "dog_sitter", label: "Dog Sitter", icon: Dog, color: "from-orange-500 to-amber-600", lightBg: "bg-orange-50", textColor: "text-orange-600" },
  { id: "cinofilo", label: "Educatori Cinofili", icon: Heart, color: "from-purple-500 to-pink-600", lightBg: "bg-purple-50", textColor: "text-purple-600" },
  { id: "pensione", label: "Pensioni", icon: Home, color: "from-emerald-500 to-teal-600", lightBg: "bg-emerald-50", textColor: "text-emerald-600" },
  { id: "assicurazione", label: "Assicurazioni", icon: Shield, color: "from-slate-500 to-gray-600", lightBg: "bg-slate-50", textColor: "text-slate-600" },
];

const professionConfig = {
  dog_sitter: { icon: Dog, color: "text-orange-600", bg: "bg-orange-50" },
  cinofilo: { icon: Heart, color: "text-purple-600", bg: "bg-purple-50" },
  pensione: { icon: Home, color: "text-emerald-600", bg: "bg-emerald-50" },
  assicurazione: { icon: Shield, color: "text-slate-600", bg: "bg-slate-50" },
};

// Sample data
const sampleServices = [
  { professional_id: "1", name: "Maria Rossi - Dog Sitter", profession_type: "dog_sitter", city: "Roma", zone: "Centro, Prati, Trastevere", rating: 4.9, reviews_count: 89, verified: true, description: "Esperienza decennale con cani di tutte le taglie. Disponibile anche per passeggiate quotidiane.", price_from: 15, phone: "+39 333 1234567" },
  { professional_id: "2", name: "Marco Bianchi - Educatore", profession_type: "cinofilo", city: "Roma", zone: "Tutta Roma e provincia", rating: 4.8, reviews_count: 56, verified: true, description: "Educatore cinofilo certificato ENCI. Addestramento base, correzione comportamentale, puppy class.", price_from: 40, phone: "+39 333 9876543" },
  { professional_id: "3", name: "Pensione Amici Pelosi", profession_type: "pensione", city: "Roma", zone: "Raccolta in tutta Roma", rating: 4.7, reviews_count: 124, verified: true, description: "Pensione per cani e gatti con ampi spazi verdi. Servizio di raccolta a domicilio disponibile.", price_from: 25, phone: "+39 06 1234567" },
  { professional_id: "4", name: "Sara Verdi - Pet Sitter", profession_type: "dog_sitter", city: "Roma", zone: "Monteverde, EUR, Garbatella", rating: 4.6, reviews_count: 34, verified: false, description: "Studentessa veterinaria, disponibile per dog sitting e cat sitting. Posso ospitare a casa mia.", price_from: 12, phone: "+39 333 5555555" },
  { professional_id: "5", name: "Luca Neri - Educatore", profession_type: "cinofilo", city: "Milano", zone: "Milano città", rating: 4.9, reviews_count: 78, verified: true, description: "Specializzato in cani problematici e aggressivi. Metodo gentile basato sul rinforzo positivo.", price_from: 50 },
];

export default function Services() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [selectedPro, setSelectedPro] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hideProfessionalBanner, setHideProfessionalBanner] = useState(false);
  const [bannerLoading, setBannerLoading] = useState(false);

  // Fetch banner preference
  useEffect(() => {
    const fetchBannerPref = async () => {
      try {
        const res = await authFetch(`${API}/user/preferences`);
        if (res.ok) {
          const data = await res.json();
          setHideProfessionalBanner(data.hide_professional_banner || false);
        }
      } catch (e) {
        // Ignore, default to showing banner
      }
    };
    fetchBannerPref();
  }, []);

  useEffect(() => {
    setIsMobile(window.innerWidth < 1024);
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    fetchProfessionals();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchProfessionals();
  }, [filterType]);

  const fetchProfessionals = async () => {
    try {
      setLoading(true);
      let url = `${API}/marketplace/professionals`;
      if (filterType !== "all") {
        url += `?profession_type=${filterType}`;
      }
      
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        // Filter only home services (not map-based like veterinario, toelettatura, negozio)
        const homeServices = data.filter(p => 
          ['dog_sitter', 'cinofilo', 'pensione', 'assicurazione'].includes(p.profession_type)
        );
        setProfessionals(homeServices.length > 0 ? homeServices : sampleServices);
      } else {
        setProfessionals(sampleServices);
      }
    } catch (error) {
      console.error('Error fetching professionals:', error);
      setProfessionals(sampleServices);
    } finally {
      setLoading(false);
    }
  };

  const filteredProfessionals = professionals.filter(pro => {
    const matchesSearch = pro.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         pro.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         pro.zone?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || pro.profession_type === filterType;
    return matchesSearch && matchesType;
  });

  // Dismiss professional banner and save preference
  const dismissBanner = async () => {
    setBannerLoading(true);
    try {
      const res = await authFetch(`${API}/user/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hide_professional_banner: true })
      });
      if (res.ok) {
        setHideProfessionalBanner(true);
        toast.success("Puoi sempre riattivarla nelle Impostazioni");
      }
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setBannerLoading(false);
    }
  };

  // Professional Banner Component - Redesigned
  const ProfessionalBanner = () => {
    if (hideProfessionalBanner) return null;
    
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1D3557] via-[#2B4A6F] to-[#1D3557] p-5 shadow-lg">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#457B9D]/30 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-[#FF6B4A]/20 to-transparent rounded-full translate-y-1/2 -translate-x-1/2" />
        
        <div className="relative flex items-center gap-4">
          {/* Icon */}
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-white/20 to-white/5 backdrop-blur flex items-center justify-center flex-shrink-0 border border-white/10">
            <Briefcase className="text-white" size={26} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold text-base mb-0.5">
              Sei un professionista?
            </h3>
            <p className="text-white/70 text-sm">
              Registrati e raggiungi nuovi clienti
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => navigate('/professional/register')}
              size="sm"
              className="bg-gradient-to-r from-[#FF6B4A] to-[#FF8F6B] hover:from-[#E55A3A] hover:to-[#E57F5B] text-white rounded-xl px-4 h-9 font-medium shadow-lg shadow-[#FF6B4A]/20"
            >
              <Sparkles size={14} className="mr-1.5" />
              Inizia
            </Button>
            <button
              onClick={dismissBanner}
              disabled={bannerLoading}
              className="text-white/50 hover:text-white/80 text-xs font-medium transition-colors"
            >
              {bannerLoading ? "..." : "No grazie"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] pb-32 overflow-x-hidden" data-testid="services-page-mobile">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-b border-black/5">
          <div className="px-4 py-4">
            <div className="flex items-center gap-3 mb-3">
              <button 
                onClick={() => navigate(-1)} 
                className="w-10 h-10 rounded-xl bg-[#F5F5F7] flex items-center justify-center flex-shrink-0"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-[#1D1D1F] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Servizi a Domicilio
                </h1>
                <p className="text-sm text-[#86868B]">Dog sitter, educatori e altro</p>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
              <Input
                type="text"
                placeholder="Cerca per nome o zona..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl bg-[#F5F5F7] border-0 h-11"
              />
            </div>
            
            {/* Category Pills */}
            <div className="flex gap-2 flex-wrap pb-1">
              <button
                onClick={() => setFilterType("all")}
                className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap ${
                  filterType === "all" ? 'bg-[#1D1D1F] text-white' : 'bg-[#F5F5F7] text-[#6E6E73]'
                }`}
              >
                Tutti
              </button>
              {categories.map(cat => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setFilterType(filterType === cat.id ? "all" : cat.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap flex items-center gap-2 ${
                      filterType === cat.id ? 'bg-[#1D1D1F] text-white' : 'bg-[#F5F5F7] text-[#6E6E73]'
                    }`}
                  >
                    <Icon size={14} />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Become a Professional Banner - Redesigned */}
        <div className="px-4 py-3">
          <ProfessionalBanner />
        </div>
        
        {/* Results */}
        <div className="px-4 py-2 overflow-hidden w-full max-w-full">
          <p className="text-sm text-[#86868B] mb-3">{filteredProfessionals.length} professionisti trovati</p>
          
          <div className="space-y-3 w-full">
            {filteredProfessionals.map(pro => {
              const config = professionConfig[pro.profession_type] || professionConfig.dog_sitter;
              const Icon = config.icon;
              
              return (
                <div 
                  key={pro.professional_id}
                  onClick={() => setSelectedPro(pro)}
                  className="bg-white rounded-2xl p-4 border border-black/5 cursor-pointer hover:shadow-md transition-all w-full"
                  data-testid={`professional-card-${pro.professional_id}`}
                >
                  {/* Header row */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-xl ${config.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={config.color} size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-bold text-[#1D1D1F] flex items-center gap-1.5 leading-tight">
                          <span className="line-clamp-1">{pro.name}</span>
                          {pro.verified && <CheckCircle size={14} className="text-blue-500 flex-shrink-0" />}
                        </h3>
                      </div>
                      <p className="text-sm text-[#86868B]">{pro.city}</p>
                    </div>
                  </div>
                  
                  {/* Zone */}
                  {pro.zone && (
                    <div className="flex items-center gap-1.5 mb-2">
                      <MapPin size={12} className="text-[#86868B] flex-shrink-0" />
                      <span className="text-xs text-[#86868B] line-clamp-1">{pro.zone}</span>
                    </div>
                  )}
                  
                  {/* Description */}
                  <p className="text-sm text-[#6E6E73] mb-3 line-clamp-2">{pro.description}</p>
                  
                  {/* Footer: Price and Rating */}
                  <div className="flex items-center justify-between pt-3 border-t border-[#F5F5F7]">
                    {pro.price_from ? (
                      <div className="bg-[#FF6B4A]/10 rounded-lg px-3 py-1.5">
                        <span className="text-[#FF6B4A] font-bold text-sm">
                          Da €{pro.price_from}
                        </span>
                        <span className="text-[#FF6B4A]/70 text-xs">/ora</span>
                      </div>
                    ) : (
                      <div />
                    )}
                    <div className="flex items-center gap-1.5 bg-[#F5F5F7] rounded-lg px-2.5 py-1.5">
                      <Star size={14} className="text-amber-400 fill-amber-400" />
                      <span className="text-sm font-semibold text-[#1D1D1F]">{pro.rating}</span>
                      <span className="text-xs text-[#86868B]">({pro.reviews_count})</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Professional Detail Dialog */}
        <Dialog open={!!selectedPro} onOpenChange={() => setSelectedPro(null)}>
          <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[400px] rounded-3xl p-0 overflow-hidden mx-4">
            {selectedPro && (
              <div>
                <div className={`p-6 bg-gradient-to-br ${categories.find(c => c.id === selectedPro.profession_type)?.color || 'from-gray-500 to-gray-600'}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                      {(() => { const Icon = professionConfig[selectedPro.profession_type]?.icon || Dog; return <Icon className="text-white" size={32} />; })()}
                    </div>
                    <div className="text-white">
                      <h2 className="text-xl font-bold flex items-center gap-2">
                        {selectedPro.name}
                        {selectedPro.verified && <CheckCircle size={18} />}
                      </h2>
                      <p className="text-white/80">{selectedPro.city}</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Star className="text-amber-400 fill-amber-400" size={18} />
                      <span className="font-bold">{selectedPro.rating}</span>
                      <span className="text-[#86868B]">({selectedPro.reviews_count} recensioni)</span>
                    </div>
                    {selectedPro.price_from && (
                      <span className="text-[#FF6B4A] font-bold">Da €{selectedPro.price_from}/ora</span>
                    )}
                  </div>
                  
                  {selectedPro.zone && (
                    <div className="bg-[#F5F5F7] rounded-xl p-3">
                      <p className="text-sm text-[#86868B] mb-1">Zone servite</p>
                      <p className="font-medium">{selectedPro.zone}</p>
                    </div>
                  )}
                  
                  <p className="text-[#6E6E73]">{selectedPro.description}</p>
                  
                  <div className="flex gap-3">
                    {selectedPro.phone && (
                      <a href={`tel:${selectedPro.phone}`} className="flex-1">
                        <Button variant="outline" className="w-full h-12 rounded-xl">
                          <Phone size={18} className="mr-2" />
                          Chiama
                        </Button>
                      </a>
                    )}
                    <Button 
                      onClick={() => { setShowBooking(true); }}
                      className="flex-1 h-12 bg-[#FF6B4A] hover:bg-[#E55A3A] rounded-xl"
                    >
                      <Calendar size={18} className="mr-2" />
                      Prenota
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        <BookingDialog 
          open={showBooking} 
          onOpenChange={setShowBooking}
          professional={selectedPro}
        />
      </div>
    );
  }

  // Desktop Layout
  return (
    <PageLayout data-testid="services-page">
      <PageHeader 
        title="Servizi a Domicilio"
        subtitle="Dog sitter, educatori cinofili e altri servizi nella tua zona"
        icon={Users}
        iconColor="#FF6B4A"
      />

      <div className="grid grid-cols-12 gap-6">
          {/* Sidebar Filters */}
          <div className="col-span-3">
            <div className="bg-white rounded-3xl p-5 shadow-sm border border-black/5 sticky top-8">
              <h2 className="font-bold text-[#1D1D1F] mb-4">Categoria</h2>
              
              <button
                onClick={() => setFilterType("all")}
                className={`w-full flex items-center gap-3 p-3 rounded-xl mb-2 transition-all ${
                  filterType === "all" ? 'bg-[#FF6B4A] text-white' : 'hover:bg-[#F5F5F7]'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  filterType === "all" ? 'bg-white/20' : 'bg-[#F5F5F7]'
                }`}>
                  <Users size={20} className={filterType === "all" ? 'text-white' : 'text-[#FF6B4A]'} />
                </div>
                <span className="font-medium">Tutti i servizi</span>
              </button>
              
              {categories.map(cat => {
                const Icon = cat.icon;
                const isSelected = filterType === cat.id;
                
                return (
                  <button
                    key={cat.id}
                    onClick={() => setFilterType(isSelected ? "all" : cat.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl mb-2 transition-all ${
                      isSelected ? `bg-gradient-to-r ${cat.color} text-white` : 'hover:bg-[#F5F5F7]'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isSelected ? 'bg-white/20' : cat.lightBg
                    }`}>
                      <Icon size={20} className={isSelected ? 'text-white' : cat.textColor} />
                    </div>
                    <span className="font-medium">{cat.label}</span>
                  </button>
                );
              })}

              {/* Search */}
              <div className="mt-6 pt-4 border-t border-black/5">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" size={16} />
                  <Input
                    type="text"
                    placeholder="Cerca per zona..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-11 pl-10 rounded-xl bg-[#F5F5F7] border-0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-black/5">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-[#1D1D1F]">
                  {filterType === "all" ? 'Tutti i servizi' : categories.find(c => c.id === filterType)?.label}
                </h2>
                <span className="text-[#86868B]">{filteredProfessionals.length} professionisti</span>
              </div>

              <div className="space-y-4">
                {filteredProfessionals.map(pro => {
                  const config = professionConfig[pro.profession_type] || professionConfig.dog_sitter;
                  const Icon = config.icon;
                  
                  return (
                    <div 
                      key={pro.professional_id}
                      className="flex items-start gap-4 p-4 rounded-2xl border border-black/5 hover:shadow-md transition-all cursor-pointer"
                      onClick={() => setSelectedPro(pro)}
                    >
                      <div className={`w-14 h-14 rounded-xl ${config.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={config.color} size={28} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-bold text-[#1D1D1F] text-lg flex items-center gap-2">
                              {pro.name}
                              {pro.verified && <CheckCircle size={16} className="text-blue-500" />}
                            </h3>
                            <p className="text-[#86868B]">{pro.city}</p>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1">
                              <Star className="text-amber-400 fill-amber-400" size={16} />
                              <span className="font-bold">{pro.rating}</span>
                              <span className="text-sm text-[#86868B]">({pro.reviews_count})</span>
                            </div>
                            {pro.price_from && (
                              <p className="text-[#FF6B4A] font-bold mt-1">Da €{pro.price_from}/ora</p>
                            )}
                          </div>
                        </div>
                        
                        {pro.zone && (
                          <div className="flex items-center gap-1 mt-2">
                            <MapPin size={14} className="text-[#86868B]" />
                            <span className="text-sm text-[#86868B]">{pro.zone}</span>
                          </div>
                        )}
                        
                        <p className="text-[#6E6E73] mt-2 line-clamp-2">{pro.description}</p>
                        
                        <div className="flex gap-2 mt-3">
                          {pro.phone && (
                            <a href={`tel:${pro.phone}`} onClick={e => e.stopPropagation()}>
                              <Button variant="outline" size="sm" className="rounded-lg">
                                <Phone size={14} className="mr-1" />
                                Chiama
                              </Button>
                            </a>
                          )}
                          <Button 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setSelectedPro(pro); setShowBooking(true); }}
                            className="bg-[#FF6B4A] hover:bg-[#E55A3A] rounded-lg"
                          >
                            <Calendar size={14} className="mr-1" />
                            Prenota
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      
      <BookingDialog 
        open={showBooking} 
        onOpenChange={setShowBooking}
        professional={selectedPro}
      />
    </PageLayout>
  );
}
